import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
  Chip,
  LinearProgress,
  Fade,
  Skeleton,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  FileDownload,
  Refresh,
  CalendarToday,
  AttachMoney,
  ShoppingCart,
  Payment,
  CreditCard,
  QrCode,
  Contactless,
  AccountBalanceWallet,
  VolunteerActivism,
  PictureAsPdf,
  TableChart,
} from "@mui/icons-material";
import { Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/ja";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";
import api from "../../services/api";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("ja");

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#0088FE", "#00C49F"];

// 決済カテゴリ別の色
const PAYMENT_CATEGORY_COLORS = {
  cash: "#4CAF50",
  credit_card: "#2196F3",
  qr_code: "#FF9800",
  transport_ic: "#9C27B0",
  retail_emoney: "#00BCD4",
  postpay: "#E91E63",
  other: "#607D8B",
};

// 決済カテゴリ別のアイコン
const PAYMENT_CATEGORY_ICONS = {
  cash: AttachMoney,
  credit_card: CreditCard,
  qr_code: QrCode,
  transport_ic: Contactless,
  retail_emoney: AccountBalanceWallet,
  postpay: Contactless,
  other: Payment,
};

const Reports = () => {
  const { storeId } = useParams();
  const theme = useTheme();
  const [reports, setReports] = useState({
    dailySales: 0,
    monthlySales: 0,
    topProducts: [],
    salesByHour: [],
    previousDailySales: 0,
    previousMonthlySales: 0,
    salesGoal: 100000,
    totalTips: 0,
    tipsCount: 0,
  });
  const [paymentReport, setPaymentReport] = useState({
    summary: { totalSales: 0, totalOrders: 0 },
    byCategory: [],
    byMethod: [],
  });
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [activePreset, setActivePreset] = useState("today");

  // L-4 FIX: エクスポートメニュー用の状態
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const getTranslatedText = (translationObj, defaultLang = "ja") => {
    if (!translationObj) return "";
    if (typeof translationObj !== "object") return translationObj;
    return (
      translationObj[defaultLang] ||
      translationObj.ja ||
      translationObj.en ||
      translationObj.zh ||
      ""
    );
  };

  const presets = [
    { key: "today", label: "今日", salesLabel: "本日の売上", getRange: () => [dayjs(), dayjs()] },
    { key: "yesterday", label: "昨日", salesLabel: "昨日の売上", getRange: () => [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")] },
    { key: "week", label: "今週", salesLabel: "今週の売上", getRange: () => [dayjs().startOf("week"), dayjs()] },
    { key: "month", label: "今月", salesLabel: "今月の売上", getRange: () => [dayjs().startOf("month"), dayjs()] },
  ];

  const getPeriodLabel = () => {
    const preset = presets.find(p => p.key === activePreset);
    if (preset) return preset.salesLabel;
    // カスタム日付範囲の場合
    if (startDate.isSame(endDate, 'day')) {
      return `${startDate.format('M/D')}の売上`;
    }
    return `${startDate.format('M/D')}〜${endDate.format('M/D')}の売上`;
  };

  const getPeriodName = () => {
    const preset = presets.find(p => p.key === activePreset);
    if (preset) return preset.label;
    if (startDate.isSame(endDate, 'day')) {
      return startDate.format('M/D');
    }
    return `${startDate.format('M/D')}〜${endDate.format('M/D')}`;
  };

  const handlePreset = (preset) => {
    setActivePreset(preset.key);
    const [start, end] = preset.getRange();
    setStartDate(start);
    setEndDate(end);
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      console.log("[Reports] Fetching orders for storeId:", storeId);

      // 注文データを取得して計算
      const ordersRes = await api.get(`/api/orders`, {
        params: { storeId }
      });
      console.log("[Reports] Raw API response:", ordersRes);
      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      console.log("[Reports] Fetched", orders.length, "orders");
      if (orders.length > 0) {
        console.log("[Reports] Sample order keys:", Object.keys(orders[0]));
        console.log("[Reports] Sample order totalAmount:", orders[0].totalAmount, "total_amount:", orders[0].total_amount, "total:", orders[0].total);
        console.log("[Reports] Sample order createdAt:", orders[0].createdAt, "created_at:", orders[0].created_at);
      }

      // CRIT-REPORT-1 FIX: 日付範囲でフィルタ（PAID + PARTIALLY_REFUNDED を売上としてカウント）
      const start = startDate.startOf('day');
      const end = endDate.endOf('day');
      const filteredOrders = orders.filter(order => {
        const orderDate = dayjs(order.createdAt || order.created_at);
        const paymentStatus = order.payment_status || order.paymentStatus;
        // PAID または PARTIALLY_REFUNDED を売上としてカウント（バックエンドと統一）
        if (paymentStatus !== 'PAID' && paymentStatus !== 'PARTIALLY_REFUNDED') return false;
        return orderDate.isSameOrAfter(start) && orderDate.isSameOrBefore(end);
      });
      console.log("[Reports] Date range:", start.format('YYYY-MM-DD'), "to", end.format('YYYY-MM-DD'));
      console.log("[Reports] Filtered orders (PAID + PARTIALLY_REFUNDED):", filteredOrders.length);

      // CRIT-REPORT-2 FIX: 期間の売上合計（返金額を差し引く）
      const periodSales = filteredOrders.reduce((sum, order) => {
        const totalAmount = order.total || order.total_amount || order.totalAmount || 0;
        const refundedAmount = order.payment_details?.refundedAmount || order.refunded_amount || 0;
        return sum + Math.max(0, totalAmount - refundedAmount);
      }, 0);
      console.log("[Reports] Period sales:", periodSales);

      // CRIT-REPORT-1/2 FIX: 月間売上（今月のすべて、PAID + PARTIALLY_REFUNDED、返金額を差し引く）
      const monthStart = dayjs().startOf('month');
      const monthlyOrders = orders.filter(order => {
        const orderDate = dayjs(order.createdAt || order.created_at);
        const paymentStatus = order.payment_status || order.paymentStatus;
        // PAID または PARTIALLY_REFUNDED を売上としてカウント
        if (paymentStatus !== 'PAID' && paymentStatus !== 'PARTIALLY_REFUNDED') return false;
        return orderDate.isSameOrAfter(monthStart);
      });
      const monthlySales = monthlyOrders.reduce((sum, order) => {
        const totalAmount = order.total || order.total_amount || order.totalAmount || 0;
        const refundedAmount = order.payment_details?.refundedAmount || order.refunded_amount || 0;
        return sum + Math.max(0, totalAmount - refundedAmount);
      }, 0);

      // 商品別売上集計
      const productSales = {};
      filteredOrders.forEach(order => {
        const items = order.items || order.order_items || [];
        items.forEach(item => {
          const productId = item.productId || item.product_id || item.id;
          const productName = item.name || item.product_name || item.product?.name || '不明';
          const quantity = item.quantity || 1;
          const price = item.price || item.unit_price || 0;
          if (!productSales[productId]) {
            productSales[productId] = { id: productId, name: productName, sales: 0, quantity: 0 };
          }
          productSales[productId].sales += price * quantity;
          productSales[productId].quantity += quantity;
        });
      });
      const topProducts = Object.values(productSales)
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 5);

      // 時間帯別売上
      const salesByHourMap = {};
      for (let h = 9; h <= 21; h++) {
        salesByHourMap[h] = { hour: h, sales: 0, orders: 0 };
      }
      // CRIT-REPORT-2 FIX: 時間帯別でも返金額を差し引く
      filteredOrders.forEach(order => {
        const hour = dayjs(order.createdAt || order.created_at).hour();
        if (salesByHourMap[hour]) {
          const totalAmount = order.total || order.total_amount || order.totalAmount || 0;
          const refundedAmount = order.payment_details?.refundedAmount || order.refunded_amount || 0;
          salesByHourMap[hour].sales += Math.max(0, totalAmount - refundedAmount);
          salesByHourMap[hour].orders += 1;
        }
      });
      const salesByHour = Object.values(salesByHourMap);

      // 投げ銭データを取得
      let totalTips = 0;
      let tipsCount = 0;
      try {
        const tipsRes = await api.get(`/api/stores/${storeId}/tips`, {
          params: {
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
          }
        });
        const tips = Array.isArray(tipsRes.data) ? tipsRes.data : [];
        tipsCount = tips.length;
        totalTips = tips.reduce((sum, tip) => sum + (tip.amount || 0), 0);
        console.log("[Reports] Tips fetched:", { count: tipsCount, total: totalTips });
      } catch (tipsError) {
        console.log("[Reports] Tips fetch failed (may not exist):", tipsError.message);
        // 注文データからtipTotalを集計（フォールバック）
        filteredOrders.forEach(order => {
          const tipTotal = order.tipTotal || order.tip_total || 0;
          if (tipTotal > 0) {
            totalTips += tipTotal;
            tipsCount += 1;
          }
        });
      }

      setReports({
        dailySales: periodSales,
        monthlySales: monthlySales,
        topProducts: topProducts,
        salesByHour: salesByHour,
        previousDailySales: 0,
        previousMonthlySales: 0,
        salesGoal: 100000,
        totalTips: totalTips,
        tipsCount: tipsCount,
      });
    } catch (error) {
      console.error("[Reports] Error fetching reports:", error);
      console.error("[Reports] Error details:", error.response?.data || error.message);
      setReports({
        dailySales: 0,
        monthlySales: 0,
        topProducts: [],
        salesByHour: [],
        previousDailySales: 0,
        previousMonthlySales: 0,
        salesGoal: 100000,
        totalTips: 0,
        tipsCount: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentReport = async () => {
    try {
      setPaymentLoading(true);

      // 注文データから決済方法別に集計
      const ordersRes = await api.get(`/api/orders`, {
        params: { storeId }
      });
      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];

      // ISSUE-9-1 FIX: 日付範囲でフィルタ（PAID + PARTIALLY_REFUNDED を売上としてカウント）
      const start = startDate.startOf('day');
      const end = endDate.endOf('day');
      const filteredOrders = orders.filter(order => {
        const orderDate = dayjs(order.createdAt || order.created_at);
        const paymentStatus = order.payment_status || order.paymentStatus;
        // ISSUE-9-1 FIX: PAID または PARTIALLY_REFUNDED を売上としてカウント（fetchReportsと統一）
        if (paymentStatus !== 'PAID' && paymentStatus !== 'PARTIALLY_REFUNDED') return false;
        return orderDate.isSameOrAfter(start) && orderDate.isSameOrBefore(end);
      });

      // 決済方法別に集計
      const methodMap = {
        'CASH': { id: 'cash', name: '現金', category: 'cash' },
        'cash': { id: 'cash', name: '現金', category: 'cash' },
        'CREDIT_CARD': { id: 'credit_card', name: 'クレジットカード', category: 'credit_card' },
        'credit_card': { id: 'credit_card', name: 'クレジットカード', category: 'credit_card' },
        'QR': { id: 'qr_code', name: 'QRコード決済', category: 'qr_code' },
        'qr_code': { id: 'qr_code', name: 'QRコード決済', category: 'qr_code' },
        'IC': { id: 'transport_ic', name: '交通系IC', category: 'transport_ic' },
      };

      const byCategory = {};
      let totalSales = 0;
      let totalOrders = 0;

      filteredOrders.forEach(order => {
        // payment_details.method から決済方法を取得（正規化後は paymentDetails.method）
        const method = order.paymentDetails?.method || order.payment_details?.method || order.paymentMethod || order.payment_method || 'cash';
        const totalAmount = order.total || order.total_amount || order.totalAmount || 0;
        // ISSUE-9-1 FIX: 返金額を差し引く（fetchReportsと統一）
        const refundedAmount = order.payment_details?.refundedAmount || order.refunded_amount || 0;
        const amount = Math.max(0, totalAmount - refundedAmount);
        const info = methodMap[method] || { id: 'other', name: method, category: 'other' };

        if (!byCategory[info.category]) {
          byCategory[info.category] = { categoryId: info.category, categoryName: info.name, amount: 0, count: 0 };
        }
        byCategory[info.category].amount += amount;
        byCategory[info.category].count += 1;
        totalSales += amount;
        totalOrders += 1;
      });

      // パーセンテージを計算
      const byCategoryArray = Object.values(byCategory).map(cat => ({
        ...cat,
        methodId: cat.categoryId,
        methodName: cat.categoryName,
        category: cat.categoryId,
        percentage: totalSales > 0 ? Math.round((cat.amount / totalSales) * 100) : 0
      }));

      console.log("[Reports] Payment by category:", byCategoryArray);

      setPaymentReport({
        success: true,
        summary: { totalSales, totalOrders },
        byCategory: byCategoryArray,
        byMethod: byCategoryArray,
      });
    } catch (error) {
      console.error("決済方法別レポートの取得に失敗しました:", error);
      setPaymentReport({
        success: false,
        summary: { totalSales: 0, totalOrders: 0 },
        byCategory: [],
        byMethod: [],
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchPaymentReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, startDate, endDate]);

  const dailyChange = useMemo(() => {
    if (!reports.previousDailySales) return 0;
    return ((reports.dailySales - reports.previousDailySales) / reports.previousDailySales) * 100;
  }, [reports.dailySales, reports.previousDailySales]);

  const monthlyChange = useMemo(() => {
    if (!reports.previousMonthlySales) return 0;
    return ((reports.monthlySales - reports.previousMonthlySales) / reports.previousMonthlySales) * 100;
  }, [reports.monthlySales, reports.previousMonthlySales]);

  const goalProgress = useMemo(() => {
    return Math.min((reports.dailySales / reports.salesGoal) * 100, 100);
  }, [reports.dailySales, reports.salesGoal]);

  // L-4 FIX: ローカルCSVエクスポート（簡易版）
  const exportCSVLocal = () => {
    const headers = ["時間帯", "注文数", "売上"];
    const rows = reports.salesByHour.map((h) => [
      `${h.hour}:00`,
      h.orders,
      h.sales,
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales_report_${startDate.format("YYYYMMDD")}_${endDate.format("YYYYMMDD")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchorEl(null);
  };

  // L-4 FIX: サーバーからCSVをダウンロード（詳細版）
  const exportCSVFromServer = async () => {
    setExportLoading(true);
    try {
      const response = await api.get(`/api/daily-report/${storeId}/export/csv`, {
        params: { date: startDate.format("YYYY-MM-DD") },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily-report_${storeId}_${startDate.format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "CSVをダウンロードしました", severity: "success" });
    } catch (error) {
      console.error("CSVエクスポートエラー:", error);
      setSnackbar({ open: true, message: "CSVダウンロードに失敗しました", severity: "error" });
    } finally {
      setExportLoading(false);
      setExportAnchorEl(null);
    }
  };

  // L-4 FIX: サーバーからPDFをダウンロード
  const exportPDF = async () => {
    setExportLoading(true);
    try {
      const response = await api.get(`/api/daily-report/${storeId}/export/pdf`, {
        params: { date: startDate.format("YYYY-MM-DD") },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily-report_${storeId}_${startDate.format("YYYY-MM-DD")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "PDFをダウンロードしました", severity: "success" });
    } catch (error) {
      console.error("PDFエクスポートエラー:", error);
      setSnackbar({ open: true, message: "PDFダウンロードに失敗しました", severity: "error" });
    } finally {
      setExportLoading(false);
      setExportAnchorEl(null);
    }
  };

  const SummaryCard = ({ title, value, change, icon: Icon, color }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 8px 24px ${alpha(color, 0.25)}`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={120} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={700} color={color}>
                ¥{value.toLocaleString()}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(color, 0.1),
            }}
          >
            <Icon sx={{ fontSize: 28, color }} />
          </Box>
        </Box>
        {!loading && change !== undefined && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            {change >= 0 ? (
              <TrendingUp sx={{ fontSize: 16, color: "success.main", mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: "error.main", mr: 0.5 }} />
            )}
            <Typography
              variant="body2"
              color={change >= 0 ? "success.main" : "error.main"}
              fontWeight={600}
            >
              {change >= 0 ? "+" : ""}
              {change.toFixed(1)}% 前日比
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            売上レポート
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* L-4 FIX: エクスポートメニュー */}
            <Tooltip title="レポートエクスポート">
              <IconButton
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                color="primary"
                disabled={exportLoading}
              >
                <FileDownload />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={exportCSVLocal}>
                <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
                <ListItemText>CSV（時間帯別）</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportCSVFromServer}>
                <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
                <ListItemText>CSV（日計表詳細）</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportPDF}>
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                <ListItemText>PDF（日計表）</ListItemText>
              </MenuItem>
            </Menu>
            <Tooltip title="データ更新">
              <IconButton onClick={fetchReports} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 期間選択 */}
        <Card sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <ButtonGroup variant="outlined" size="small">
                {presets.map((preset) => (
                  <Button
                    key={preset.key}
                    variant={activePreset === preset.key ? "contained" : "outlined"}
                    onClick={() => handlePreset(preset)}
                    startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
                  >
                    {preset.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <DatePicker
                  label="開始日"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    setActivePreset("");
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
                <Typography>〜</Typography>
                <DatePicker
                  label="終了日"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                    setActivePreset("");
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* サマリーカード */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title={getPeriodLabel()}
              value={reports.dailySales}
              change={dailyChange}
              icon={AttachMoney}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="今月の売上"
              value={reports.monthlySales}
              change={monthlyChange}
              icon={TrendingUp}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {getPeriodName()}の目標達成率
                </Typography>
                {loading ? (
                  <Skeleton width="100%" height={60} />
                ) : (
                  <>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {goalProgress.toFixed(0)}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={goalProgress}
                      sx={{
                        mt: 1,
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      目標: ¥{reports.salesGoal.toLocaleString()}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  注文数
                </Typography>
                {loading ? (
                  <Skeleton width={80} height={40} />
                ) : (
                  <Typography variant="h4" fontWeight={700}>
                    {reports.salesByHour.reduce((sum, h) => sum + (h.orders || 0), 0)}件
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <ShoppingCart sx={{ fontSize: 16, color: "text.secondary", mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {getPeriodName()}の総注文数
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 投げ銭サマリー */}
        {reports.totalTips > 0 && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha("#FF6B6B", 0.1)} 0%, ${alpha("#FF6B6B", 0.05)} 100%)`,
                  border: `1px solid ${alpha("#FF6B6B", 0.2)}`,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 24px ${alpha("#FF6B6B", 0.25)}`,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {getPeriodName()}の投げ銭
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: "#FF6B6B" }}>
                        ¥{reports.totalTips.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha("#FF6B6B", 0.1),
                      }}
                    >
                      <VolunteerActivism sx={{ fontSize: 28, color: "#FF6B6B" }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {reports.tipsCount}件の投げ銭
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        売上＋投げ銭 合計
                      </Typography>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        ¥{(reports.dailySales + reports.totalTips).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}
                    >
                      <AttachMoney sx={{ fontSize: 28, color: theme.palette.warning.main }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      売上 ¥{reports.dailySales.toLocaleString()} + 投げ銭 ¥{reports.totalTips.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* グラフセクション */}
        <Grid container spacing={3}>
          {/* 時間帯別売上 */}
          <Grid item xs={12} lg={8}>
            <Fade in={!loading}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    時間帯別売上
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={reports.salesByHour}>
                        <defs>
                          <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis
                          dataKey="hour"
                          tickFormatter={(val) => `${val}時`}
                          stroke={theme.palette.text.secondary}
                        />
                        <YAxis
                          tickFormatter={(val) => `¥${(val / 1000).toFixed(0)}k`}
                          stroke={theme.palette.text.secondary}
                        />
                        <RechartsTooltip
                          formatter={(value) => [`¥${value.toLocaleString()}`, "売上"]}
                          labelFormatter={(label) => `${label}:00`}
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="sales"
                          stroke={theme.palette.primary.main}
                          fillOpacity={1}
                          fill="url(#colorSales)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* 人気商品 円グラフ */}
          <Grid item xs={12} lg={4}>
            <Fade in={!loading}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    人気商品ランキング
                  </Typography>
                  <Box sx={{ height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={reports.topProducts.slice(0, 5)}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="sales"
                          nameKey="name"
                        >
                          {reports.topProducts.slice(0, 5).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name, entry) => [
                            `¥${value.toLocaleString()}`,
                            getTranslatedText(entry.payload.name),
                          ]}
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {reports.topProducts.slice(0, 5).map((product, index) => (
                      <Box
                        key={product.id || index}
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: COLORS[index % COLORS.length],
                            mr: 1,
                          }}
                        />
                        <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                          {getTranslatedText(product.name)}
                        </Typography>
                        <Chip
                          label={`¥${product.sales?.toLocaleString() || 0}`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* 時間帯別注文数 棒グラフ */}
          <Grid item xs={12}>
            <Fade in={!loading}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    時間帯別注文数
                  </Typography>
                  <Box sx={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reports.salesByHour}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                        <XAxis
                          dataKey="hour"
                          tickFormatter={(val) => `${val}時`}
                          stroke={theme.palette.text.secondary}
                        />
                        <YAxis stroke={theme.palette.text.secondary} />
                        <RechartsTooltip
                          formatter={(value) => [`${value}件`, "注文数"]}
                          labelFormatter={(label) => `${label}:00`}
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                          }}
                        />
                        <Bar
                          dataKey="orders"
                          fill={theme.palette.secondary.main}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* 詳細テーブル */}
          <Grid item xs={12}>
            <Fade in={!loading}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  時間帯別詳細
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>時間帯</TableCell>
                        <TableCell align="right">注文数</TableCell>
                        <TableCell align="right">売上</TableCell>
                        <TableCell align="right">平均単価</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.salesByHour.map((hour) => (
                        <TableRow
                          key={hour.hour}
                          sx={{
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                          }}
                        >
                          <TableCell>{hour.hour}:00</TableCell>
                          <TableCell align="right">{hour.orders}件</TableCell>
                          <TableCell align="right">¥{hour.sales?.toLocaleString() || 0}</TableCell>
                          <TableCell align="right">
                            ¥{hour.orders > 0 ? Math.round(hour.sales / hour.orders).toLocaleString() : 0}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Fade>
          </Grid>

          {/* 決済方法別売上セクション */}
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 2, mb: 2 }}>
              決済方法別売上
            </Typography>
          </Grid>

          {/* カテゴリ別円グラフ */}
          <Grid item xs={12} md={6}>
            <Fade in={!paymentLoading}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    カテゴリ別構成比
                  </Typography>
                  {paymentLoading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : paymentReport.byCategory.length === 0 ? (
                    <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography color="text.secondary">データがありません</Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={paymentReport.byCategory}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              paddingAngle={3}
                              dataKey="amount"
                              nameKey="categoryName"
                            >
                              {paymentReport.byCategory.map((entry) => (
                                <Cell
                                  key={entry.categoryId}
                                  fill={PAYMENT_CATEGORY_COLORS[entry.categoryId] || "#607D8B"}
                                />
                              ))}
                            </Pie>
                            <RechartsTooltip
                              formatter={(value, name) => [`¥${value.toLocaleString()}`, name]}
                              contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        {paymentReport.byCategory.map((category) => {
                          const IconComponent = PAYMENT_CATEGORY_ICONS[category.categoryId] || Payment;
                          return (
                            <Box
                              key={category.categoryId}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: alpha(PAYMENT_CATEGORY_COLORS[category.categoryId] || "#607D8B", 0.1),
                              }}
                            >
                              <IconComponent
                                sx={{
                                  fontSize: 20,
                                  color: PAYMENT_CATEGORY_COLORS[category.categoryId] || "#607D8B",
                                  mr: 1,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {category.categoryName}
                              </Typography>
                              <Chip
                                label={`${category.percentage.toFixed(1)}%`}
                                size="small"
                                sx={{
                                  bgcolor: PAYMENT_CATEGORY_COLORS[category.categoryId] || "#607D8B",
                                  color: "#fff",
                                  mr: 1,
                                }}
                              />
                              <Typography variant="body2" fontWeight={600}>
                                ¥{category.amount.toLocaleString()}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* 決済方法別棒グラフ */}
          <Grid item xs={12} md={6}>
            <Fade in={!paymentLoading}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    決済方法別売上
                  </Typography>
                  {paymentLoading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : paymentReport.byMethod.length === 0 ? (
                    <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography color="text.secondary">データがありません</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={paymentReport.byMethod.slice(0, 10)}
                          layout="vertical"
                          margin={{ left: 20, right: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.1)} />
                          <XAxis
                            type="number"
                            tickFormatter={(val) => `¥${(val / 1000).toFixed(0)}k`}
                            stroke={theme.palette.text.secondary}
                          />
                          <YAxis
                            type="category"
                            dataKey="methodName"
                            width={100}
                            tick={{ fontSize: 12 }}
                            stroke={theme.palette.text.secondary}
                          />
                          <RechartsTooltip
                            formatter={(value, name, entry) => [
                              `¥${value.toLocaleString()} (${entry.payload.count}件)`,
                              "売上",
                            ]}
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                            {paymentReport.byMethod.slice(0, 10).map((entry) => (
                              <Cell
                                key={entry.methodId}
                                fill={PAYMENT_CATEGORY_COLORS[entry.category] || "#607D8B"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* 決済方法別詳細テーブル */}
          <Grid item xs={12}>
            <Fade in={!paymentLoading}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  決済方法別詳細
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>決済方法</TableCell>
                        <TableCell>カテゴリ</TableCell>
                        <TableCell align="right">件数</TableCell>
                        <TableCell align="right">金額</TableCell>
                        <TableCell align="right">構成比</TableCell>
                        <TableCell align="right">平均単価</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentReport.byMethod.map((method) => (
                        <TableRow
                          key={method.methodId}
                          sx={{
                            "&:hover": { bgcolor: alpha(PAYMENT_CATEGORY_COLORS[method.category] || "#607D8B", 0.05) },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: PAYMENT_CATEGORY_COLORS[method.category] || "#607D8B",
                                  mr: 1,
                                }}
                              />
                              {method.methodName}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={paymentReport.byCategory.find((c) => c.categoryId === method.category)?.categoryName || method.category}
                              size="small"
                              sx={{
                                bgcolor: alpha(PAYMENT_CATEGORY_COLORS[method.category] || "#607D8B", 0.1),
                                color: PAYMENT_CATEGORY_COLORS[method.category] || "#607D8B",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{method.count}件</TableCell>
                          <TableCell align="right">¥{method.amount.toLocaleString()}</TableCell>
                          <TableCell align="right">{method.percentage.toFixed(1)}%</TableCell>
                          <TableCell align="right">
                            ¥{method.count > 0 ? Math.round(method.amount / method.count).toLocaleString() : 0}
                          </TableCell>
                        </TableRow>
                      ))}
                      {paymentReport.byMethod.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">データがありません</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Box>

      {/* L-4 FIX: エクスポート結果通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default Reports;
